import React, { useState } from 'react';
import { Form, Input, Button, Alert, Typography, Steps, Divider } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, BankOutlined, EyeInvisibleOutlined, EyeTwoTone, CrownOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import PlanSelection from './PlanSelection';

const { Title, Text } = Typography;
const { Step } = Steps;

const RegisterForm = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (values) => {
    clearError();
    // Include selected plan in registration data
    const registrationData = {
      ...values,
      selectedPlan: selectedPlan
    };
    
    const result = await register(registrationData);
    
    if (result.success) {
      // Show success message and redirect to login
      navigate('/login', { 
        state: { 
          message: 'Registration successful! Please sign in to continue.',
          type: 'success'
        }
      });
    }
  };

  const handleFormChange = () => {
    if (error) {
      clearError();
    }
  };

  const validateEmail = (_, value) => {
    // No need to check for empty value - the required rule handles that
    if (!value) {
      return Promise.resolve();
    }

    // Strict email validation regex
    // Allows: letters, numbers, dots, hyphens, underscores, and plus signs
    // Disallows: consecutive dots, special characters like #, leading/trailing dots
    const emailRegex = /^[a-zA-Z0-9]+([._+-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;

    if (!emailRegex.test(value)) {
      return Promise.reject(new Error('Please enter a valid email address'));
    }

    // Additional checks for common invalid patterns
    if (value.includes('..')) {
      return Promise.reject(new Error('Email cannot contain consecutive dots'));
    }

    if (value.split('@').length !== 2) {
      return Promise.reject(new Error('Email must contain exactly one @ symbol'));
    }

    return Promise.resolve();
  };

  const validateSubdomain = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter a subdomain'));
    }
    if (!/^[a-z0-9-]+$/.test(value)) {
      return Promise.reject(new Error('Subdomain can only contain lowercase letters, numbers, and hyphens'));
    }
    if (value.length < 3) {
      return Promise.reject(new Error('Subdomain must be at least 3 characters'));
    }
    return Promise.resolve();
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter a password'));
    }
    if (value.length < 8) {
      return Promise.reject(new Error('Password must be at least 8 characters'));
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(value)) {
      return Promise.reject(new Error('Password must contain uppercase, lowercase, number and special character'));
    }
    return Promise.resolve();
  };

  const steps = [
    {
      title: 'Personal Info',
      description: 'Your details'
    },
    {
      title: 'Company Info',
      description: 'Company setup'
    },
    {
      title: 'Choose Plan',
      description: 'Select subscription'
    },
    {
      title: 'Security',
      description: 'Password setup'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
      <div className="flex min-h-screen w-full">
        {/* Left Column - Logo and Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-black bg-opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center items-center w-full px-8 xl:px-12">
            {/* Logo */}
            <div className="mb-12">
              <img 
                src="/qqcrm-logo.png" 
                alt="QQCRM Logo" 
                className="h-16 xl:h-20 w-auto filter brightness-0 invert"
              />
            </div>
            
            {/* Branding Content */}
            <div className="text-center text-white max-w-md">
              <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                Welcome to QQCRM
              </h1>
              <p className="text-lg xl:text-xl text-blue-100 mb-10 leading-relaxed">
                The most powerful CRM solution for modern businesses. 
                Streamline your sales, manage leads, and grow your revenue.
              </p>
              
              {/* Features */}
              <div className="space-y-5 text-left">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Advanced Lead Management</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Sales Pipeline Automation</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Real-time Analytics & Reports</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Team Collaboration Tools</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Registration Form */}
        <div className="w-full lg:w-1/2 flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center lg:text-left">
              {/* Mobile Logo */}
              <div className="lg:hidden mb-6">
                <img 
                  src="/qqcrm-logo.png" 
                  alt="QQCRM Logo" 
                  className="h-12 w-auto mx-auto"
                />
              </div>
              
              <Title level={2} className="text-gray-900 dark:text-gray-100 mb-2">
                Start your free trial
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 text-lg">
                Create your CRM account in minutes
              </Text>
            </div>
          </div>

          {/* Steps */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-4">
            <Steps current={currentStep} className="w-full" size="small">
              {steps.map((step, index) => (
                <Step key={index} title={step.title} description={step.description} />
              ))}
            </Steps>
          </div>

          {/* Form Content */}
          <div className="flex-1 bg-white dark:bg-gray-800 px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto w-full">
            {error && (
              <Alert
                message="Registration Failed"
                description={error}
                type="error"
                showIcon
                closable
                onClose={clearError}
                className="mb-6"
                role="alert"
                aria-live="polite"
              />
            )}

            {/* Form Container */}
            <div className="w-full">
            <Form
              form={form}
              name="register"
              onFinish={handleSubmit}
              onValuesChange={handleFormChange}
              layout="vertical"
              size="large"
              autoComplete="on"
            >
        {/* Step 1: Personal Information */}
        <div className={currentStep === 0 ? 'block' : 'hidden'}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[
              { required: true, message: 'Please enter your first name' },
              { max: 50, message: 'First name cannot exceed 50 characters' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="Enter your first name"
              autoComplete="given-name"
              className="focus-visible:focus"
            />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[
              { required: true, message: 'Please enter your last name' },
              { max: 50, message: 'Last name cannot exceed 50 characters' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="Enter your last name"
              autoComplete="family-name"
              className="focus-visible:focus"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { validator: validateEmail }
            ]}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Input
              prefix={<MailOutlined className="text-gray-400" />}
              placeholder="Enter your email"
              autoComplete="email"
              className="focus-visible:focus"
            />
          </Form.Item>
        </div>

        {/* Step 2: Company Information */}
        <div className={currentStep === 1 ? 'block' : 'hidden'}>
          <Form.Item
            name="companyName"
            label="Company Name"
            rules={[
              { required: true, message: 'Please enter your company name' },
              { max: 100, message: 'Company name cannot exceed 100 characters' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              prefix={<BankOutlined className="text-gray-400" />}
              placeholder="Enter your company name"
              autoComplete="organization"
              className="focus-visible:focus"
            />
          </Form.Item>

          <Form.Item
            name="subdomain"
            label="Subdomain"
            rules={[{ validator: validateSubdomain }]}
            extra="This will be your unique CRM URL: https://your-subdomain.crmpro.com"
          >
            <Input
              placeholder="your-company"
              addonBefore="https://"
              addonAfter=".crmpro.com"
              className="focus-visible:focus"
            />
          </Form.Item>
        </div>

        {/* Step 3: Plan Selection */}
        <div className={currentStep === 2 ? 'block' : 'hidden'}>
          <PlanSelection 
            selectedPlan={selectedPlan}
            onPlanSelect={setSelectedPlan}
          />
        </div>

        {/* Step 4: Security */}
        <div className={currentStep === 3 ? 'block' : 'hidden'}>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ validator: validatePassword }]}
            extra="Must contain uppercase, lowercase, number and special character"
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="Create a strong password"
              autoComplete="new-password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              className="focus-visible:focus"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="Confirm your password"
              autoComplete="new-password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              className="focus-visible:focus"
            />
          </Form.Item>
        </div>

        {/* Navigation Buttons */}
        <Form.Item className="mb-4">
          <div className="flex justify-between">
            {currentStep > 0 && (
              <Button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="touch-target focus-visible:focus"
              >
                Previous
              </Button>
            )}
            
            {currentStep < steps.length - 1 ? (
              <Button
                type="primary"
                onClick={async () => {
                  try {
                    // Validate only visible fields of the current step
                    if (currentStep === 0) {
                      await form.validateFields(['firstName', 'lastName', 'email']);
                    } else if (currentStep === 1) {
                      await form.validateFields(['companyName']);
                    } else if (currentStep === 2) {
                      if (!selectedPlan) {
                        return; // Don’t allow to proceed if no plan selected
                      }
                    }
                    // If validation passes, go to next step
                    setCurrentStep(currentStep + 1);
                  } catch (err) {
                    // Validation failed → don't go next
                    console.log('Validation failed:', err);
                  }
                }}
                disabled={currentStep === 2 && !selectedPlan}
                className="ml-auto touch-target focus-visible:focus"
              >
                Next
              </Button>
            ) : (
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                className="ml-auto h-12 px-8 text-base font-medium touch-target focus-visible:focus"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            )}
          </div>
            </Form.Item>
            </Form>

            <Divider className="my-6">
              <Text className="text-gray-500 dark:text-gray-400 text-sm">
                Already have an account?
              </Text>
            </Divider>

            <div className="text-center lg:text-left">
              <Link 
                to="/login" 
                className="text-primary-600 hover:text-primary-500 font-medium focus-visible:focus"
              >
                Sign in instead
              </Link>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
